/* Donut — §9.4. One <circle> per slice: stroke-dasharray carries the arc length,
   stroke-dashoffset walks the cumulative total, and the whole thing is rotated
   -90deg so the first slice starts at twelve o'clock. A 1.6px gap separates
   neighbours. No charting library is involved. */

import { useId } from 'react';
import { CASH_COLOR } from '../lib/colors';
import { money, pct } from '../lib/format';

export interface Slice {
  key: string;
  label: string;
  /** What to write around the ring when the full label is too long to sit
      beside a 4% slice. "Futures" is a heading, not a name. */
  short?: string;
  /** The company's own mark, drawn beside its label. A `data:` URI. */
  logo?: string;
  value: number;
  weight: number;
  color: string;
}

interface Props {
  slices: Slice[];
  selected: string | null;
  onSelect: (key: string | null) => void;
  onFlip: () => void;
  centre: { label: string; value: string; sub?: string };
  /* What tapping the centre does, said by whoever placed the ring. It used to
     flip a hero card over to the performance chart; on a screen where that
     chart is already above the ring there is nothing to flip to, and a hint
     promising history it cannot show is worse than no hint at all. */
  flip?: { hint: string; label: string };
  currency: 'USD' | 'PLN';
  /** Names and weights written around the ring. Off gives back the bare ring
      and the room it used to take. */
  labels?: boolean;
}

const SIZE = 240;
const STROKE = 26;
const GAP_PX = 1.6;
const R = (SIZE - STROKE) / 2;
const CIRC = 2 * Math.PI * R;

/* ---------- labels around the ring ---------- */

/* Room outside the ring for the names. Wider than it is tall, because a label
   is a horizontal thing: the ones that reach furthest are those at nine and
   three o'clock, and giving the top and bottom the same margin would shrink the
   ring to buy space nothing uses. Both margins are symmetric, so the centre of
   the drawing is still the centre of the element and the hole still sits under
   the button overlaying it. The horizontal margin also carries the small mark
   drawn beside each name, which is why it is wider than the text alone needs. */
const PAD_X = 90;
const PAD_Y = 26;

/* Below this a slice is thinner than the text describing it, and the labels
   start stacking into an unreadable ladder. The list under the chart names
   every position, so nothing is lost by leaving these off — the alternative,
   drawing them anyway, loses the ones that are worth reading. */
const LABEL_MIN_PCT = 3;

const TICK_OUT = 6;    // how far the leader leaves the ring
const TEXT_OFF = 7;    // gap between the leader's end and the text
const MIN_ROW = 13;    // closest two labels may sit on one side
/* The mark beside a label, as a diameter in viewBox units. Small on purpose:
   this is a chart of proportions and the marks are there to find a slice by
   eye, not to be looked at. Anything larger starts competing with the ring it
   is describing. */
const MARK_R = 5.4;

interface Label {
  key: string;
  ticker: string;
  logo: string | undefined;
  weight: string;
  color: string;
  right: boolean;
  x0: number; y0: number;   // on the ring
  x1: number; y1: number;   // the elbow
  xText: number;
  y: number;                // adjusted for its neighbours
}

/** Where each label wants to sit, before anything is moved out of the way. */
function place(arcs: Array<Slice & { start: number; fraction: number }>): Label[] {
  const c = SIZE / 2;
  const outer = R + STROKE / 2;
  return arcs
    .filter((a) => a.weight >= LABEL_MIN_PCT)
    .map((a) => {
      const angle = (a.start + a.fraction / 2) * 2 * Math.PI - Math.PI / 2;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);
      const right = cos >= 0;
      const x1 = c + cos * (outer + TICK_OUT);
      return {
        key: a.key,
        ticker: a.short ?? a.label,
        logo: a.logo,
        weight: pct(a.weight),
        color: a.color,
        right,
        x0: c + cos * outer,
        y0: c + sin * outer,
        x1,
        y1: c + sin * (outer + TICK_OUT),
        xText: x1 + (right ? TEXT_OFF : -TEXT_OFF),
        y: c + sin * (outer + TICK_OUT),
      };
    });
}

/* Two labels on the same side at nearly the same angle would print on top of
   each other. Each side is walked top to bottom and anything too close to the
   one above is pushed down; if that runs the column off the bottom, the whole
   column moves up by the overshoot. The leader line is drawn to wherever the
   label ended up, so it still points at its own slice. */
function spread(labels: Label[]): Label[] {
  const bottom = SIZE + PAD_Y - 6;
  const top = -PAD_Y + 6;
  for (const side of [true, false]) {
    const column = labels.filter((l) => l.right === side).sort((a, b) => a.y - b.y);
    for (let i = 1; i < column.length; i++) {
      const above = column[i - 1]!;
      const here = column[i]!;
      if (here.y - above.y < MIN_ROW) here.y = above.y + MIN_ROW;
    }
    const last = column[column.length - 1];
    if (last && last.y > bottom) {
      const shift = last.y - bottom;
      for (const l of column) l.y = Math.max(top, l.y - shift);
    }
  }
  return labels;
}

export function Donut({ slices, selected, onSelect, onFlip, centre, flip, labels = true }: Props) {
  const titleId = useId();
  const visible = slices.filter((s) => s.weight > 0);
  const total = visible.reduce((sum, s) => sum + s.weight, 0) || 100;

  let cursor = 0;
  const arcs = visible.map((s) => {
    const fraction = s.weight / total;
    const start = cursor / CIRC;
    const length = CIRC * fraction;
    // Never let the gap eat a slice whole — a sliver still has to be visible.
    const drawn = Math.max(length - GAP_PX, Math.min(length, 0.6));
    const arc = { ...s, length: drawn, offset: -cursor, start, fraction };
    cursor += length;
    return arc;
  });

  const written = labels ? spread(place(arcs)) : [];
  const box = labels
    ? `${-PAD_X} ${-PAD_Y} ${SIZE + PAD_X * 2} ${SIZE + PAD_Y * 2}`
    : `0 0 ${SIZE} ${SIZE}`;

  /* The accessible label lists the top holdings so the chart is not colour-only
     information (§11). */
  const spoken = visible
    .slice(0, 6)
    .map((s) => `${s.label} ${s.weight.toFixed(1)} percent`)
    .join(', ');

  return (
    <div className="donut-wrap" data-labels={labels ? 'true' : 'false'}>
      <svg
        className="donut"
        viewBox={box}
        role="img"
        aria-labelledby={titleId}
      >
        <title id={titleId}>
          {`Holdings by weight. ${visible.length} positions. Largest: ${spoken}.`}
        </title>
        <g transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}>
          {arcs.map((a) => (
            <circle
              key={a.key}
              className="donut-slice"
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={R}
              fill="none"
              stroke={a.color}
              strokeWidth={selected === a.key ? STROKE + 5 : STROKE}
              strokeDasharray={`${a.length} ${CIRC - a.length}`}
              strokeDashoffset={a.offset}
              strokeLinecap="butt"
              data-dim={selected !== null && selected !== a.key}
              onClick={() => onSelect(selected === a.key ? null : a.key)}
            >
              <title>{`${a.label}: ${money(a.value)}, ${pct(a.weight)}`}</title>
            </circle>
          ))}
        </g>

        {/* Written outside the rotated group, because the text has to stay
            upright whatever angle its slice sits at. */}
        {written.map((l) => (
          <g
            key={l.key}
            className="donut-label"
            data-dim={selected !== null && selected !== l.key}
            onClick={() => onSelect(selected === l.key ? null : l.key)}
          >
            <polyline
              className="donut-leader"
              points={`${l.x0},${l.y0} ${l.x1},${l.y1} ${l.xText + (l.right ? -2 : 2)},${l.y}`}
              stroke={l.color}
              fill="none"
            />
            {/* The mark sits on the ring side of its own label, so the eye
                runs mark, name, weight outwards on the right and inwards on the
                left. Clipped to a circle for the same reason the row tiles are:
                most of these images are a square of the company's own
                background, and a square of it out here reads as a sticker
                rather than as a mark. */}
            {l.logo ? (
              <>
                <clipPath id={`${titleId}-${l.key}`}>
                  <circle
                    cx={l.xText + (l.right ? MARK_R : -MARK_R)}
                    cy={l.y}
                    r={MARK_R}
                  />
                </clipPath>
                <image
                  href={l.logo}
                  x={l.xText + (l.right ? 0 : -MARK_R * 2)}
                  y={l.y - MARK_R}
                  width={MARK_R * 2}
                  height={MARK_R * 2}
                  clipPath={`url(#${titleId}-${l.key})`}
                  preserveAspectRatio="xMidYMid slice"
                />
              </>
            ) : null}
            <text
              x={l.xText + (l.logo ? (l.right ? MARK_R * 2 + 3 : -(MARK_R * 2 + 3)) : 0)}
              y={l.y}
              textAnchor={l.right ? 'start' : 'end'}
              dominantBaseline="middle"
            >
              <tspan className="donut-label-name" fill={l.color}>{l.ticker}</tspan>
              <tspan className="donut-label-pct" dx="4">{l.weight}</tspan>
            </text>
          </g>
        ))}
      </svg>

      {/* The centre is a button in its own right — §7.1 flipped it to the chart,
          and where that no longer applies the caller says what it does. */}
      <button className="donut-centre" onClick={onFlip}
              aria-label={flip?.label ?? 'Show the performance chart'}>
        <span className="k">{centre.label}</span>
        <span className="v num">{centre.value}</span>
        {centre.sub ? <span className="s num">{centre.sub}</span> : null}
        {(flip ? flip.hint : 'TAP FOR HISTORY')
          ? <span className="flip-hint">{flip ? flip.hint : 'TAP FOR HISTORY'}</span>
          : null}
      </button>
    </div>
  );
}

export { CASH_COLOR };
